/**
 * The one place a `LabourCapWarning` becomes a `Badge` — Task T2-E-web. Shared by
 * `RosterScreen.tsx` (a day cell, a week total) and `RosterDrawer.tsx` (a fresh
 * assign's `warnings`, an assignment row's own) so the icon/severity/label mapping
 * and the dedupe rule live in exactly one component, never re-derived per call site.
 *
 * `Badge` is always given `LABOUR_CAP_WARNING_PRESENTATION[code].icon` — never
 * `SEVERITY_ICON[…severity]` — the exact review finding T2-B's own fix round 1 named
 * (`EMPLOYEE_STATUS_PRESENTATION`'s own header repeats the same rule).
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import type { LabourCapWarning } from '../../api/roster';
import { dedupeWarnings, warningDetailKey, warningPresentationFor } from '../../roster/roster';
import { Badge } from '../../states/Badge';

export function WarningChip({
  warning,
  testId,
}: {
  readonly warning: LabourCapWarning;
  readonly testId: string;
}): ReactNode {
  const { t } = useI18n();
  // `warning.code` is a value off the network, typed as the closed
  // `LabourCapWarningCode` union but not runtime-validated against it —
  // `warningPresentationFor` (`roster/roster.ts`) is the one place that looks
  // it up, so an unrecognised seventh code falls back to a generic chip
  // rather than throwing and taking the whole roster grid down.
  const presentation = warningPresentationFor(warning.code);
  const detail = warningDetailKey(warning);
  return (
    <Badge
      severity={presentation.severity}
      icon={presentation.icon}
      label={t(presentation.labelKey)}
      {...(detail === null ? {} : { title: t(detail.key, detail.args) })}
      testId={testId}
    />
  );
}

/** `null` when there is nothing to show — a day, a week or an assignment with no warning. */
export function WarningChipList({
  warnings,
  testPrefix,
}: {
  readonly warnings: readonly LabourCapWarning[];
  readonly testPrefix: string;
}): ReactNode {
  const deduped = dedupeWarnings(warnings);
  if (deduped.length === 0) return null;
  return (
    <ul className="wp-flags" data-testid={`${testPrefix}-warnings`}>
      {deduped.map((warning) => (
        <li key={warning.code}>
          <WarningChip warning={warning} testId={`${testPrefix}-warning-${warning.code}`} />
        </li>
      ))}
    </ul>
  );
}
